import React, { useState } from 'react';
import { Loader2, Play, Sparkles, List } from 'lucide-react';
import { WheelData } from '../../../../types';
import LegendModal from './LegendModal';

interface WheelCanvasProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  wheelSize: number;
  isSpinning: boolean;
  formName: string;
  wheelData: WheelData;
  theme: {
    background_color: string;
    text_color: string;
    font_family: string;
    logo_url?: string;
  };
  onSpin: () => void;
}

const WheelCanvas: React.FC<WheelCanvasProps> = ({
  canvasRef,
  wheelSize,
  isSpinning,
  formName,
  wheelData,
  theme,
  onSpin
}) => {
  const [showLegend, setShowLegend] = useState(false);

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 relative"
      style={{
        background: `linear-gradient(to bottom right, ${theme.background_color}, ${theme.background_color}dd)`,
        color: theme.text_color,
        fontFamily: theme.font_family
      }}
    >
      {/* Botão flutuante de legendas */}
      <button
        onClick={() => setShowLegend(true)}
        className="fixed bottom-6 right-6 z-30 bg-white text-indigo-600 p-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 border-2 border-indigo-600"
        title="Ver prêmios disponíveis"
      >
        <List className="w-6 h-6" />
      </button>

      {/* Modal de legendas */}
      <LegendModal
        isOpen={showLegend}
        onClose={() => setShowLegend(false)}
        wheelData={wheelData}
      />

      <div className="max-w-lg w-full text-center">
        <div className="mb-8">
          {theme.logo_url && (
            <div className="mb-4 flex justify-center">
              <img
                src={theme.logo_url}
                alt="Logo"
                className="h-12 max-w-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          <h1 className="text-2xl font-bold mb-2">
            Boa sorte, {formName}!
          </h1>
          <p style={{ opacity: 0.9 }}>
            {isSpinning ? 'A roda está girando...' : 'Clique no botão para girar a roda'}
          </p>
        </div>

        <div className="relative mx-auto mb-8" style={{ width: wheelSize, height: wheelSize }}>
          {/* Sombra de fundo para profundidade */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
              filter: 'blur(20px)',
              transform: 'scale(0.95)',
            }}
          />

          {/* Moldura externa da roda */}
          <div
            className="absolute inset-0 rounded-full z-10"
            style={{
              border: '8px solid rgba(255, 255, 255, 0.9)',
              boxShadow: '0 0 0 4px rgba(0, 0, 0, 0.1), inset 0 0 20px rgba(0, 0, 0, 0.2)',
            }}
          />

          <canvas
            id="wheelCanvas"
            ref={canvasRef}
            width={wheelSize}
            height={wheelSize}
            className="mx-auto relative z-10"
            style={{
              filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))',
            }}
          />

          {/* Centro da roda - botão play grande */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20"
            style={{
              width: '80px',
              height: '80px',
            }}
          >
            <div
              className="w-full h-full rounded-full bg-white flex items-center justify-center"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.8)',
                border: '4px solid rgba(255, 255, 255, 0.9)',
              }}
            >
              {!isSpinning && (
                <Play
                  onClick={onSpin}
                  className="h-10 w-10 cursor-pointer"
                  style={{
                    color: theme.background_color,
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                  }}
                />
              )}
            </div>
          </div>

          {/* Ponteiro indicador - mais visível */}
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-20">
            <img src='/assets/icons/pin.svg' alt="Ponteiro" className="h-12 w-12" />
          </div>
        </div>

        {!isSpinning ? (
          <button
            onClick={onSpin}
            className="bg-white font-bold py-5 px-10 rounded-full text-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 border-4"
            style={{
              color: theme.background_color,
              borderColor: theme.background_color,
              background: 'linear-gradient(to bottom, #FFFFFF, #F5F5F5)',
            }}
          >
            <Sparkles className="h-7 w-7 mr-3 inline animate-pulse" />
            GIRAR
          </button>
        ) : (
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Girando...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WheelCanvas;
